import { createContext, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import { ThemeProvider } from "@emotion/react";
import { lightMode } from "../theme";
import axios from "axios";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_DETAILS_KEY,
} from "../components/constants";
import { ACCESS_TOKEN_URL } from "../GlobalUrls";
import Loader from "../components/Common/Loader";
import Toaster from "../components/Common/ToasterComponent/Toaster";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [user, setUser] = useLocalStorage(USER_DETAILS_KEY, null);

  const [accessToken, setAccessToken] = useLocalStorage(ACCESS_TOKEN_KEY, null);
  const [refreshToken, setRefreshToken] = useLocalStorage(
    REFRESH_TOKEN_KEY,
    null
  );

  const [showToaster, setshowToaster] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // call this function when you want to authenticate the user
  const login = async (/** @type {any} */ payload) => {
    setLoading(true);
    try {
      await axios.post(ACCESS_TOKEN_URL, payload).then((response) => {
        setUser(response.data);
        setAccessToken(response.data?.access);
        setRefreshToken(response.data?.refresh);
        navigate("/");
        setLoading(false);
        return true;
      });
    } catch (error) {
      setshowToaster({
        open: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Login failed!",
        severity: "error",
      });
      setLoading(false);
      return false;
    }
  };

  // call this function to sign out logged in user
  const logout = () => {
    setUser(null);
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const handleSessionExpiry = () => {
    setshowToaster({
      open: true,
      message: "Your session has expired. Successfully logged out!",
      severity: "warning",
    });
    logout();
  };

  const handleCloseToaster = () => {
    setshowToaster({
      open: false,
      message: "",
      severity: "",
    });
  };

  const value = useMemo(
    () => ({
      login,
      logout,
      handleSessionExpiry,
      setshowToaster,
      setLoading,
      loading,
      accessToken,
      refreshToken,
      setAccessToken,
      setRefreshToken,
      user,
    }),
    // eslint-disable-next-line
    [user, accessToken, refreshToken, loading]
  );

  return (
    <ThemeProvider theme={lightMode}>
      <Loader isLoading={loading} />
      <Toaster
        open={showToaster.open}
        severity={showToaster.severity}
        message={showToaster.message}
        handleClose={handleCloseToaster}
      />
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </ThemeProvider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
